import "../../../css/DocumentManagement.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, message, Input, Select } from "antd";
import { PlusOutlined, ReloadOutlined, GlobalOutlined } from "@ant-design/icons";
import { getDocuments } from "../../../services/polly/documentService";
import DocumentTable from "./DocumentTable";
import AddDocumentModal from "./AddDocumentModal";
import PermissionGate from "../../../auth/PermissionGate";
import { PERMISSION_POLLY_CRAWL_SOURCES_MANAGE } from "../../../auth/permissions";

const { Search } = Input;
const { Option } = Select;

const isYouTube = (uri) => /youtube\.com\/watch|youtu\.be\//.test(uri || '');
const isWeb = (uri) => /^https?:\/\//.test(uri || '');

function inferType(uri) {
  if (isYouTube(uri)) return 'video';
  if (isWeb(uri)) return 'web';
  if (/\.(pdf|docx|doc)$/i.test(uri || '')) return 'upload';
  return 'text';
}

function adaptDoc(doc) {
  return {
    id: doc.normalizedDocumentId,
    name: doc.name,
    url: doc.uri,
    type: inferType(doc.uri),
    chunks: doc.chunks ? Object.keys(doc.chunks).length : 0,
    status: doc.status || 'active',
    lastIngested: doc.lastCheckedAt || null,
    hitRate: null,
    version: doc.version || 1,
  };
}

export default function DocumentManagement() {
  const history = useHistory();
  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const searchDebounce = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const fetchDocuments = useCallback(async (p, type, status, search) => {
    setLoading(true);
    try {
      const res = await getDocuments({
        page: p - 1, // backend is 0-indexed
        size: pageSize,
        type: type !== "all" ? type : undefined,
        status: status !== "all" ? status : undefined,
        search: search || undefined,
      });
      if (!mountedRef.current) return;
      const docs = Array.isArray(res.content) ? res.content : [];
      setDocuments(docs.map(adaptDoc));
      setTotalElements(res.totalElements || 0);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to fetch documents:", err);
      message.error("Failed to load documents");
    }
    if (mountedRef.current) setLoading(false);
  }, [pageSize]);

  useEffect(() => {
    fetchDocuments(page, typeFilter, statusFilter, searchText);
  }, [page, typeFilter, statusFilter, searchText, fetchDocuments]);

  const handleTypeChange = (val) => {
    setTypeFilter(val);
    setPage(1);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  const handleSearch = (val) => {
    setSearchText(val);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      handleSearch(val);
    }, 400);
  };

  const handleAddSuccess = () => {
    setModalVisible(false);
    fetchDocuments(page, typeFilter, statusFilter, searchText);
  };

  const handleRefresh = () => {
    fetchDocuments(page, typeFilter, statusFilter, searchText);
  };

  const showEmpty = !loading && documents.length === 0 && typeFilter === "all" && statusFilter === "active" && !searchText;

  return (
    <div className="doc-mgmt-container">
      <div className="doc-mgmt-breadcrumb" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>Polly &rsaquo; <span>Document Vault</span></div>
        <PermissionGate permission={PERMISSION_POLLY_CRAWL_SOURCES_MANAGE}>
          <Button size="small" icon={<GlobalOutlined />} onClick={() => history.push("/polly/crawl-sources")}>
            Manage Crawl Sources
          </Button>
        </PermissionGate>
      </div>
      <div className="doc-mgmt-body">
        {showEmpty ? (
          <div className="doc-mgmt-empty">
            <p>No documents in the vault yet. Add your first document.</p>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
              Add Document
            </Button>
          </div>
        ) : (
          <>
            <div className="doc-mgmt-filter-bar">
              <Select value={typeFilter} onChange={handleTypeChange} size="small">
                <Option value="all">All Types</Option>
                <Option value="web">Web</Option>
                <Option value="upload">Upload</Option>
                <Option value="video">Video</Option>
                <Option value="text">Text</Option>
              </Select>
              <Select value={statusFilter} onChange={handleStatusChange} size="small">
                <Option value="all">All Statuses</Option>
                <Option value="active">Active</Option>
                <Option value="archived">Archived</Option>
              </Select>
              <Search
                placeholder="Search documents..."
                allowClear
                size="small"
                onSearch={handleSearch}
                onChange={handleSearchChange}
                style={{ maxWidth: 240 }}
              />
              <span className="doc-mgmt-doc-count">{totalElements} document{totalElements !== 1 ? "s" : ""}</span>
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                Refresh
              </Button>
              <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                Add Document
              </Button>
            </div>
            <DocumentTable
              documents={documents}
              total={totalElements}
              page={page}
              pageSize={pageSize}
              loading={loading}
              onPageChange={(p) => setPage(p)}
              onRefresh={() => fetchDocuments(page, typeFilter, statusFilter, searchText)}
            />
          </>
        )}
      </div>

      <AddDocumentModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
}
